import { Button, Flex } from "@chakra-ui/react";
import { RiAddLine } from "@remixicon/react";
import { AnimatePresence } from "framer-motion";
import { ReactNode, useCallback } from "react";

import MotionBox from "@/common/components/MotionBox";
import { SegmentSupport } from "@/common/components/SegmentPicker";
import {
  AdjustAttributeFieldsCallback,
  FilterGroup as FilterGroupModel,
  UIFilter,
  UIFilterType,
} from "@reflag/shared/filter";
import { SegmentWithMetadataDTO } from "@reflag/shared/segmentAPI";
import { toBase64 } from "@reflag/shared/utils/base64";

import { AndOrList } from "../AndOrList";
import FilterDropdown from "./FilterDropdown";
import { FilterItem } from "./FilterItem";

export type FilterGroupOnChange = (
  value: UIFilter,
  action: {
    action: "create" | "update" | "delete";
    filter: UIFilter;
  },
) => void;

type FilterGroupProps = {
  value: FilterGroupModel<UIFilter> | undefined;
  types?: UIFilterType[];
  isReadOnly?: boolean;
  flatten?: boolean;
  adjustAttributeFields?: AdjustAttributeFieldsCallback;
  segmentFilterFn?: (segment: SegmentWithMetadataDTO) => SegmentSupport;
  segmentUnsupportedReason?: ReactNode;
  showItemsIcons?: boolean;
  warnings?: Partial<Record<UIFilterType, ReactNode>>;
  onChange?: FilterGroupOnChange;
  context?: FilterContextType;
};

export type FilterContextType = "flag" | "segment";

export const SupportedCompanyFilterTypes: UIFilterType[] = [
  "companyAttribute",
  "companyIds",
  "segment",
  "featureTargeting",
  "featureMetric",
];

export function FilterGroup({
  types = SupportedCompanyFilterTypes,
  value,
  context,
  warnings,
  segmentUnsupportedReason,
  flatten = false,
  showItemsIcons = true,
  isReadOnly = false,
  adjustAttributeFields,
  segmentFilterFn,
  onChange,
}: FilterGroupProps) {
  const renderFilter = useCallback(
    (filter: UIFilter | undefined, index = 0, depth = 0) => {
      // Create a "unique" id based on the filter object to render correct filter drop-down
      const key = toBase64(filter);

      if (!filter || filter.type === "group") {
        const render = (
          <>
            {filter?.filters?.length ? (
              <AnimatePresence initial={false}>
                <AndOrList
                  conjunction={filter.operator}
                  direction="horizontal"
                  flatten={flatten}
                  flexWrap="wrap"
                >
                  {filter.filters.map((f, index) =>
                    renderFilter(f, index, depth + 1),
                  )}
                </AndOrList>
              </AnimatePresence>
            ) : (
              isReadOnly && (
                <FilterItem
                  filter={undefined}
                  showIcon={showItemsIcons}
                  size="sm"
                />
              )
            )}
            {depth === 0 && !isReadOnly && (
              <FilterDropdown
                key={key}
                adjustAttributeFields={adjustAttributeFields}
                context={context}
                filter={undefined}
                segmentFilterFn={segmentFilterFn}
                segmentUnsupportedReason={segmentUnsupportedReason}
                types={types}
                warnings={warnings}
                onSave={(newFilter) => {
                  // Does not currently handle nested groups
                  const newFilterGroup = {
                    type: "group" as const,
                    operator: value?.operator ?? "and",
                    filters: value?.filters
                      ? [...value.filters, newFilter]
                      : [newFilter],
                  };
                  onChange?.(newFilterGroup, {
                    action: "create",
                    filter: newFilter,
                  });
                }}
              >
                {({ triggerRef, toggle }) => (
                  <MotionBox transition={{ duration: 0.1 }} layout>
                    <Button
                      ref={triggerRef}
                      flexShrink={0}
                      iconSpacing={1}
                      leftIcon={<RiAddLine size={16} />}
                      pl={1}
                      pr={2}
                      variant="ghostDimmed"
                      onClick={toggle}
                    >
                      Add filter
                    </Button>
                  </MotionBox>
                )}
              </FilterDropdown>
            )}
          </>
        );

        if (flatten) {
          return render;
        }

        return (
          <Flex key={key} align="center" flexWrap="wrap" gap={2}>
            {render}
          </Flex>
        );
      } else if (filter.type !== "negation") {
        if (isReadOnly) {
          return (
            <FilterItem filter={filter} showIcon={showItemsIcons} size="sm" />
          );
        }

        return (
          <FilterDropdown
            key={key}
            adjustAttributeFields={adjustAttributeFields}
            context={context}
            filter={filter}
            segmentFilterFn={segmentFilterFn}
            segmentUnsupportedReason={segmentUnsupportedReason}
            types={types}
            warnings={warnings}
            onSave={(updatedFilter) => {
              // Does not currently handle nested groups
              const newFilterGroup = {
                type: "group" as const,
                operator: value?.operator ?? "and",
                filters: value?.filters?.map((f, idx) =>
                  idx === index ? updatedFilter : f,
                ),
              };
              onChange?.(newFilterGroup, {
                action: "update",
                filter: updatedFilter,
              });
            }}
          >
            {({ triggerRef, toggle }) => (
              <FilterItem
                ref={triggerRef}
                filter={filter}
                showIcon={showItemsIcons}
                size="sm"
                showRemove
                onClick={() => toggle()}
                onRemove={() => {
                  // Does not currently handle nested groups
                  const newFilterGroup = {
                    type: "group" as const,
                    operator: value?.operator ?? "and",
                    filters: value?.filters?.filter((_, idx) => idx !== index),
                  };
                  onChange?.(newFilterGroup, {
                    action: "delete",
                    filter: filter,
                  });
                }}
              />
            )}
          </FilterDropdown>
        );
      }
    },
    [
      flatten,
      isReadOnly,
      showItemsIcons,
      adjustAttributeFields,
      context,
      segmentFilterFn,
      segmentUnsupportedReason,
      types,
      warnings,
      value?.operator,
      value?.filters,
      onChange,
    ],
  );

  const rendered = renderFilter(value, 0, 0);

  if (flatten) {
    return rendered;
  }

  return (
    <Flex alignItems="center" flexWrap="wrap" gap={2}>
      {rendered}
    </Flex>
  );
}
